import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import {
  Mail, Phone, MapPin,
  MessageSquare, Send, CheckCircle, ExternalLink
} from 'lucide-react';
import { useContact } from '../hooks/useContact';
import { useToast } from '../hooks/useToast';
import { contactInfo } from '../data/contact-info';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';

const Contact = () => {
  const contact = useContact();
  const toast = useToast();
  const location = useLocation();
  const [currentView, setCurrentView] = useState('form'); // form, success

  useEffect(() => {
    document.title = 'Contact Us - Pragyavan Wellness Centre';

    // Check for pre-filled message from donation page
    if (location.state?.prefilledMessage) {
      contact.updateContactData({
        message: location.state.prefilledMessage,
        subject: 'donation' // Set subject to donation for these inquiries
      });
    }
  }, [location]);

  const subjectOptions = [
    { value: '', label: 'Select a subject' },
    { value: 'general', label: 'General Inquiry' },
    { value: 'booking', label: 'Booking & Appointments' },
    { value: 'services', label: 'Service Information' },
    { value: 'donation', label: 'Donation & Support' },
    { value: 'feedback', label: 'Feedback & Suggestions' },
    { value: 'other', label: 'Other' }
  ];

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!contact.canSubmit()) {
      toast.showError('Please fill required fields', 'Name, email, and message are required');
      return;
    }

    try {
      await contact.submitForm();
      setCurrentView('success');
      toast.showSuccess('Message sent successfully!', 'We\'ll get back to you within 24 hours');
    } catch (error) {
      toast.showError('Failed to send message', error.message);
    }
  };

  const handleQuickAction = (action, data) => {
    switch (action) {
      case 'call':
        window.location.href = `tel:${data}`;
        break;
      case 'whatsapp':
        window.open(`https://wa.me/${data.replace(/\s/g, '').replace('+', '')}`, '_blank');
        break;
      case 'email':
        window.location.href = `mailto:${data}`;
        break;
      default:
        // Unknown action
        break;
    }
  };

  const renderContactForm = () => (
    <div className="grid lg:grid-cols-2 gap-16 items-start">
      {/* Contact Form */}
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pragyavan-gold/10 text-pragyavan-brown text-xs font-medium mb-4">
            <MessageSquare className="w-3 h-3" />
            <span>We're Here to Help</span>
          </div>
          <h1 className="text-4xl font-display font-bold text-pragyavan-brown mb-4">Get in Touch</h1>
          <p className="text-warm-gray text-lg leading-relaxed">We'd love to hear from you. Send us a message.</p>
        </div>

        <Card padding="large" className="shadow-xl shadow-pragyavan-brown/5 border border-white/60 bg-white/80 backdrop-blur-sm relative overflow-hidden">
          {/* Decorative gradients */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-pragyavan-gold/15 via-pragyavan-gold/5 to-transparent rounded-full blur-2xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-sage-green/10 to-transparent rounded-full blur-2xl pointer-events-none" />

          <form onSubmit={handleFormSubmit} className="space-y-6 relative z-10">
            <div className="grid md:grid-cols-2 gap-6">
              <Input
                label="Full Name"
                type="text"
                required
                placeholder="Enter your full name"
                value={contact.contactData.name}
                onChange={(e) => contact.updateContactData({ name: e.target.value })}
                error={contact.errors.name}
              />

              <Input
                label="Email Address"
                type="email"
                required
                placeholder="your@email.com"
                value={contact.contactData.email}
                onChange={(e) => contact.updateContactData({ email: e.target.value })}
                error={contact.errors.email}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Input
                label="Phone Number"
                type="tel"
                placeholder="+91 8904568399"
                value={contact.contactData.phone}
                onChange={(e) => contact.updateContactData({ phone: e.target.value })}
                error={contact.errors.phone}
              />

              <Select
                label="Subject"
                value={contact.contactData.subject}
                onChange={(e) => contact.updateContactData({ subject: e.target.value })}
                options={subjectOptions}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-pragyavan-brown mb-2">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                className={`w-full px-4 py-4 border rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 ease-out resize-none bg-white/70 backdrop-blur-sm hover:bg-white/90 hover:border-pragyavan-gold/40 placeholder:text-warm-gray/50 ${contact.errors.message
                  ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20 bg-red-50/30'
                  : 'border-warm-gray/20 focus:border-pragyavan-gold focus:ring-pragyavan-gold/20 focus:bg-white'
                  }`}
                rows={6}
                placeholder="Tell us how we can help you..."
                value={contact.contactData.message}
                onChange={(e) => contact.updateContactData({ message: e.target.value })}
              />
              {contact.errors.message && (
                <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-red-500"></span>
                  {contact.errors.message}
                </p>
              )}
              <div className="mt-2 flex justify-between text-xs">
                <span className="text-warm-gray/70">Be specific about your inquiry for a faster response</span>
                <span className={`font-medium ${contact.getCharacterCount('message') > 900 ? 'text-amber-500' : 'text-warm-gray/70'}`}>
                  {contact.getCharacterCount('message')}/1000
                </span>
              </div>
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full shadow-lg shadow-pragyavan-gold/20"
              disabled={!contact.canSubmit() || contact.isSubmitting}
              loading={contact.isSubmitting}
            >
              {!contact.isSubmitting && <Send className="w-4 h-4 mr-2" />}
              {contact.isSubmitting ? 'Sending Message...' : 'Send Message'}
            </Button>
          </form>
        </Card>
      </motion.div>

      {/* Contact Information - Simplified */}
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pragyavan-gold/10 text-pragyavan-brown text-xs font-medium mb-4">
            <MapPin className="w-3 h-3" />
            <span>Visit Us</span>
          </div>
          <h1 className="text-4xl font-display font-bold text-pragyavan-brown mb-4">Contact Information</h1>
          <p className="text-warm-gray text-lg leading-relaxed">Get in touch with us through any of these channels.</p>
        </div>

        <Card padding="large" className="border-0 shadow-xl shadow-pragyavan-brown/5 bg-white/80 backdrop-blur-sm relative overflow-hidden">
          {/* Decorative background gradient */}
          <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-bl from-pragyavan-gold/10 via-pragyavan-gold/5 to-transparent rounded-full blur-2xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-sage-green/10 to-transparent rounded-full blur-2xl pointer-events-none" />

          <Card.Body className="space-y-0 relative z-10">
            {/* Address */}
            <motion.div
              whileHover={{ x: 4 }}
              className="flex items-start gap-5 p-5 -mx-2 rounded-2xl hover:bg-pragyavan-gold/5 transition-all duration-300 group cursor-pointer border-b border-warm-gray/10"
            >
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-pragyavan-gold/20 to-pragyavan-gold/5 flex items-center justify-center flex-shrink-0 shadow-sm group-hover:shadow-md group-hover:from-pragyavan-gold/30 transition-all duration-300">
                <MapPin className="w-5 h-5 text-pragyavan-gold" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-pragyavan-brown mb-2 text-base group-hover:text-pragyavan-gold transition-colors duration-300">Address</h4>
                {contactInfo.address.lines.map((line, index) => (
                  <p key={index} className="text-pragyavan-brown/70 text-sm leading-relaxed">{line}</p>
                ))}
              </div>
            </motion.div>

            {/* Phone */}
            <motion.div
              whileHover={{ x: 4 }}
              className="flex items-start gap-5 p-5 -mx-2 rounded-2xl hover:bg-sage-green/5 transition-all duration-300 group border-b border-warm-gray/10"
            >
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sage-green/20 to-sage-green/5 flex items-center justify-center flex-shrink-0 shadow-sm group-hover:shadow-md group-hover:from-sage-green/30 transition-all duration-300">
                <Phone className="w-5 h-5 text-sage-green" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-pragyavan-brown mb-3 text-base group-hover:text-sage-green transition-colors duration-300">Phone</h4>
                <a
                  href={`tel:${contactInfo.phone.primary.number}`}
                  className="block text-pragyavan-brown hover:text-pragyavan-gold transition-colors mb-3 group/link"
                >
                  <p className="font-semibold text-lg mb-0.5 group-hover/link:text-pragyavan-gold transition-colors">{contactInfo.phone.primary.display}</p>
                  <p className="text-xs text-warm-gray/70 font-medium uppercase tracking-wider">{contactInfo.phone.primary.type}</p>
                </a>
                <a
                  href={`https://wa.me/${contactInfo.phone.whatsapp.number.replace(/\s/g, '').replace('+', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-pragyavan-brown hover:text-green-600 transition-colors group/link"
                >
                  <p className="font-semibold text-lg mb-0.5 group-hover/link:text-green-600 transition-colors">{contactInfo.phone.whatsapp.display}</p>
                  <p className="text-xs text-warm-gray/70 font-medium uppercase tracking-wider">{contactInfo.phone.whatsapp.type}</p>
                </a>
              </div>
            </motion.div>

            {/* Email */}
            <motion.div
              whileHover={{ x: 4 }}
              className="flex items-start gap-5 p-5 -mx-2 rounded-2xl hover:bg-pragyavan-brown/5 transition-all duration-300 group"
            >
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-pragyavan-brown/15 to-pragyavan-brown/5 flex items-center justify-center flex-shrink-0 shadow-sm group-hover:shadow-md group-hover:from-pragyavan-brown/25 transition-all duration-300">
                <Mail className="w-5 h-5 text-pragyavan-brown" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-pragyavan-brown mb-2 text-base group-hover:text-pragyavan-earth transition-colors duration-300">Email</h4>
                <a
                  href={`mailto:${contactInfo.email.addresses[0].email}`}
                  className="text-pragyavan-brown hover:text-pragyavan-gold transition-colors inline-flex items-center gap-2 group/link"
                >
                  <p className="font-medium text-base group-hover/link:text-pragyavan-gold transition-colors">{contactInfo.email.addresses[0].email}</p>
                </a>
              </div>
            </motion.div>
          </Card.Body>
        </Card>
      </motion.div>
    </div>
  );

  const renderGoogleMap = () => (
    <motion.div
      className="mt-20"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pragyavan-gold/10 text-pragyavan-brown text-xs font-medium mb-4">
          <MapPin className="w-3 h-3" />
          <span>Our Location</span>
        </div>
        <h2 className="text-4xl font-display font-bold text-pragyavan-brown mb-3">Find Us</h2>
        <p className="text-warm-gray max-w-xl mx-auto">Airport Perimeter Road, Behind Adinatheshwara Temple, Adyapady, Bajpe Proper, Karnataka</p>
      </div>

      <Card className="border border-white/60 shadow-xl shadow-pragyavan-brown/5 bg-white/80 backdrop-blur-sm overflow-hidden relative">
        {/* Decorative corner elements */}
        <div className="absolute top-0 left-0 w-24 h-24 bg-gradient-to-br from-pragyavan-gold/10 to-transparent rounded-br-full pointer-events-none z-10" />
        <div className="absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-tl from-sage-green/10 to-transparent rounded-tl-full pointer-events-none z-10" />

        <Card.Body className="p-0">
          <div className="w-full h-[400px] lg:h-[500px] overflow-hidden">
            <iframe
              title="Pragyavan Wellness Centre Location"
              src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d31106.644436145154!2d74.8782896!3d12.9506875!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba3570077931925%3A0xdca2363f29627e00!2sPragyavan%20Wellness%20Centre!5e0!3m2!1sen!2sin!4v1763892253198!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="grayscale-[20%] contrast-[1.05]"
            />
          </div>
          <div className="p-6 text-center bg-gradient-to-r from-transparent via-pragyavan-gold/5 to-transparent border-t border-warm-gray/10">
            <Button
              variant="outline"
              onClick={() => window.open(contactInfo.address.googleMapsLink || 'https://maps.app.goo.gl/HnTXfg3sfV18zcG77', '_blank')}
              className="hover:bg-pragyavan-brown hover:text-white hover:border-pragyavan-brown transition-all duration-300 shadow-sm hover:shadow-md"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open in Google Maps
            </Button>
          </div>
        </Card.Body>
      </Card>
    </motion.div>
  );

  const renderSuccessMessage = () => {
    const result = contact.submitResult;

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center space-y-8 max-w-2xl mx-auto py-12"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
        >
          <div className="w-24 h-24 mx-auto bg-sage-green/10 rounded-full flex items-center justify-center">
            <CheckCircle className="w-12 h-12 text-sage-green" />
          </div>
        </motion.div>

        <div>
          <h1 className="text-4xl font-display font-bold text-pragyavan-brown mb-4">Message Sent Successfully!</h1>
          <p className="text-warm-gray text-lg">Thank you for reaching out to us. We'll get back to you soon.</p>
        </div>

        <Card className="text-left shadow-glass border-0 bg-white/80 backdrop-blur-sm">
          <Card.Header className="border-b border-warm-gray/10 pb-4">
            <h3 className="text-lg font-semibold text-pragyavan-brown text-center">Message Summary</h3>
          </Card.Header>
          <Card.Body className="space-y-4 pt-4">
            <div className="flex justify-between">
              <span className="text-warm-gray">From</span>
              <span className="font-medium text-pragyavan-brown">{result.sender.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-warm-gray">Email</span>
              <span className="font-medium text-pragyavan-brown">{result.sender.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-warm-gray">Subject</span>
              <span className="font-medium text-pragyavan-brown">{result.subject}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-warm-gray/10">
              <span className="text-warm-gray">Message ID</span>
              <span className="font-mono text-sm text-pragyavan-brown bg-warm-gray/10 px-2 py-0.5 rounded">{result.messageId}</span>
            </div>
            {result.message && (
              <div className="pt-4 border-t border-warm-gray/10">
                <p className="text-sm text-warm-gray mb-2">Your message:</p>
                <p className="text-sm text-pragyavan-brown bg-white/50 p-4 rounded-xl italic">"{result.message}"</p>
              </div>
            )}
          </Card.Body>
        </Card>

        <Card background="cream" className="border-0">
          <Card.Body>
            <h4 className="font-semibold text-pragyavan-brown mb-4 text-lg">What happens next?</h4>
            <div className="space-y-3 text-sm">
              <div className="flex items-center p-3 bg-white/50 rounded-xl">
                <CheckCircle className="w-5 h-5 text-sage-green mr-3 flex-shrink-0" />
                <span>We'll review your message within 2 hours</span>
              </div>
              <div className="flex items-center p-3 bg-white/50 rounded-xl">
                <CheckCircle className="w-5 h-5 text-sage-green mr-3 flex-shrink-0" />
                <span>You'll receive a response within {result.expectedResponse}</span>
              </div>
              <div className="flex items-center p-3 bg-white/50 rounded-xl">
                <CheckCircle className="w-5 h-5 text-sage-green mr-3 flex-shrink-0" />
                <span>Check your email for our reply at <span className="font-medium">{result.sender.email}</span></span>
              </div>
            </div>
          </Card.Body>
        </Card>

        <div className="flex justify-center space-x-4 pt-4">
          <Button variant="outline" onClick={() => {
            contact.resetForm();
            setCurrentView('form');
          }}>
            Send Another Message
          </Button>
          <Button onClick={() => window.location.href = '/'}>
            Return Home
          </Button>
        </div>
      </motion.div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="relative min-h-screen"
    >
      {/* Page background decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-pragyavan-gold/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 right-0 w-80 h-80 bg-sage-green/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-1/4 w-64 h-64 bg-pragyavan-brown/3 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 pt-24 pb-12 relative z-10">
        {currentView === 'form' ? (
          <>
            {renderContactForm()}
            {renderGoogleMap()}
          </>
        ) : (
          renderSuccessMessage()
        )}
      </div>
    </motion.div>
  );
};

export default Contact;